import { BufferAttribute, BufferGeometry, Line3, Mesh, Vector3 } from "three";
import { GeometryUtils, StyleUtils, VertexSemantic } from "../../../utils/internal";
import { GeometryNode } from "./internal";
/**
 * Creates a point geometry
 *
 * @class PointNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class LineStringNode extends GeometryNode {
    /**
     * @method constructor
     * @param {number[]} coordinates position of box in lv95 [m]
     * @param material
     * @param parent
     */
    constructor(coordinates, material, parent) {
        super(null);
        this.is2dfeature = false;
        this.parent = parent;
        this.coordinates = coordinates.slice();
        if (coordinates[0].length == 2) {
            //this is a 2d-feature without height!
            this.is2dfeature = true;
            for (let i = 0; i < coordinates.length; i++) {
                this.coordinates[i] = coordinates[i].slice(0);
                this.coordinates[i][2] = 0;
            }
        }
        this._centroid = [0, 0, 0];
        this.vertexSemantic = new VertexSemantic("v3");
        this.material = StyleUtils.CreateLineMaterial(material.line, this.parent.scene.engine);
    }
    init(traversalState) {
        super.init(traversalState);
        this.needsupdate = true;
        this.rebuildBufferUnit();
    }
    render(traversalState) {
        super.render(traversalState);
        if (this.object3D != null)
            if (!this.object3D.visible)
                return;
        if (this.is2dfeature) {
            //calc the height if no height is given
            if (traversalState.groundPlaneUpdated || this.needsupdate) {
                //update the z values
                for (let i = 0; i < this.coordinates.length; i++) {
                    const p1 = new Vector3(this.coordinates[i][0], this.coordinates[i][1], -8000);
                    const p2 = new Vector3(this.coordinates[i][0], this.coordinates[i][1], 8000);
                    const l1 = new Line3(p1, p2);
                    const target = new Vector3();
                    const pi = traversalState.groundPlane.intersectLine(l1, target);
                    if (pi != null) {
                        this.coordinates[i][2] = pi.z;
                    }
                }
                this.needsupdate = true;
            }
        }
        if (this._centroid != traversalState.scene.engine.centroid || this.needsupdate) {
            this.needsupdate = false;
            this._centroid = traversalState.scene.engine.centroid;
            this.rebuildBufferUnit();
        }
    }
    show() {
        this.object3D.visible = true;
        this.needsupdate = true;
    }
    hide() {
        this.object3D.visible = false;
        this.needsupdate = true;
    }
    setStyle(material) {
        this.material = StyleUtils.CreateLineMaterial(material.line, this.parent.scene.engine);
        this.iMesh.material = this.material;
        this.iMesh.material.needsUpdate = true;
        this.needsupdate = true;
    }
    pick(mx, my) {
        //line-picking is not supported by three.js so this is a home-made alternative
        const p = [];
        for (let i = 0; i < this.coordinates.length; i++) {
            p.push(new Vector3(this.coordinates[i][0], this.coordinates[i][1], this.coordinates[i][2]));
        }
        const pinfo = GeometryUtils.pick3dline(p, mx, my, this.parent.scene.getMeasureHandler());
        // console.log(pinfo);
        if (pinfo != null) {
            return [
                {
                    distance: pinfo["distance"],
                    distanceToRay: pinfo["distance"],
                    face: null,
                    index: pinfo["pointindex"],
                    object: this.object3D,
                    point: pinfo["mp"],
                },
            ];
        }
        return [];
    }
    updateCoordinates(coordinates) {
        this.coordinates = coordinates.slice();
        if (this.is2dfeature) {
            for (let i = 0; i < coordinates.length; i++) {
                this.coordinates[i] = coordinates[i].slice(0);
                this.coordinates[i][2] = 0;
            }
        }
        this.needsupdate = true;
    }
    destroy() {
        this.coordinates = null;
        this._centroid = null;
        this.geometry = null;
        this.object3D = null;
        this.iMesh = null;
        this.vertexSemantic = null;
        this.parent = null;
        this.lastpickinfo = null;
        this.vertices = null;
    }
    rebuildBufferUnit() {
        this.vertices = GeometryUtils.BuildLineGeometries(this.coordinates, this.vertexSemantic);
        const positions = [];
        const colors = [];
        const startP = [];
        const endP = [];
        const lineVertices = [];
        const indices = [];
        const uvs = [];
        // interate vertices
        if (this.object3D != null) {
            const obj = this.object3D;
            const vertices = this.vertices;
            if (obj.visible && vertices.length > 0) {
                for (let h = 0; h < vertices.length; h++) {
                    // determine start or end point
                    const pointFlag = vertices[h][vertices[h].length - 1];
                    if (pointFlag == 0) {
                        // startpoint
                        positions.push(vertices[h][vertices[h].length - 7] - this._centroid[0] || 0, vertices[h][vertices[h].length - 6] - this._centroid[1] || 0, vertices[h][vertices[h].length - 5] - this._centroid[2] || 0);
                    }
                    else {
                        // endpoint
                        positions.push(vertices[h][vertices[h].length - 4] - this._centroid[0] || 0, vertices[h][vertices[h].length - 3] - this._centroid[1] || 0, vertices[h][vertices[h].length - 2] - this._centroid[2] || 0);
                    }
                    startP.push(vertices[h][vertices[h].length - 7] - this._centroid[0] || 0, vertices[h][vertices[h].length - 6] - this._centroid[1] || 0, vertices[h][vertices[h].length - 5] - this._centroid[2] || 0);
                    endP.push(vertices[h][vertices[h].length - 4] - this._centroid[0] || 0, vertices[h][vertices[h].length - 3] - this._centroid[1] || 0, vertices[h][vertices[h].length - 2] - this._centroid[2] || 0);
                    // quad vertex (0, 1 ,-1)
                    lineVertices.push(vertices[h][this.vertexSemantic.vertices[0]], vertices[h][this.vertexSemantic.vertices[1]], vertices[h][this.vertexSemantic.vertices[2]]);
                    indices.push(vertices[h][vertices[h].length - 8]);
                    uvs.push(vertices[h][vertices[h].length - 10], vertices[h][vertices[h].length - 9]);
                    if (this.vertexSemantic.hasColor) {
                        colors.push(vertices[h][this.vertexSemantic.colors[0]], vertices[h][this.vertexSemantic.colors[1]], vertices[h][this.vertexSemantic.colors[2]]);
                    }
                    else {
                        colors.push(1.0, 0.0, 0.0);
                    }
                }
            }
        }
        // create first
        if (this.object3D == null) {
            this.geometry = new BufferGeometry();
            // commong attirbutes
            this.geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
            this.geometry.setAttribute("start", new BufferAttribute(new Float32Array(startP), 3));
            this.geometry.setAttribute("end", new BufferAttribute(new Float32Array(endP), 3));
            this.geometry.setAttribute("linevertex", new BufferAttribute(new Float32Array(lineVertices), 3));
            this.geometry.setAttribute("uv", new BufferAttribute(new Float32Array(uvs), 2));
            this.geometry.setAttribute("color", new BufferAttribute(new Float32Array(colors), 3));
            this.geometry.computeBoundingSphere();
            this.iMesh = new Mesh(this.geometry, this.material);
            this.iMesh.visible = this.parent.isVisible();
            this.object3D = this.iMesh;
        }
        else {
            // use exisitng
            this.geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
            this.geometry.setAttribute("start", new BufferAttribute(new Float32Array(startP), 3));
            this.geometry.setAttribute("end", new BufferAttribute(new Float32Array(endP), 3));
            this.geometry.setAttribute("linevertex", new BufferAttribute(new Float32Array(lineVertices), 3));
            this.geometry.setAttribute("uv", new BufferAttribute(new Float32Array(uvs), 2));
            this.geometry.setAttribute("color", new BufferAttribute(new Float32Array(colors), 3));
            this.geometry.computeBoundingSphere();
        }
    }
}
//# sourceMappingURL=linestringnode.js.map